import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleHasMembersError, MembersCountCell } from '../../components/RemoveMember';
import api from '../../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../../components/ui';

/**
 * Publisher list view for Practices (design/019). Sibling of CoursesAdminPage.
 * Practices are top-level Library entities with per-Practice pricing and
 * many-to-many Course associations (association is discovery only, not access).
 */
export default function PracticesAdminPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const { loading, error, data, reload } = useAsync(async () => {
    const list = await api.adminListPractices();
    return Array.isArray(list) ? list : [];
  }, []);

  const newPractice = async () => {
    setCreating(true);
    try {
      const p = await api.adminCreatePractice({
        title: 'Untitled practice', status: 'DRAFT', primaryType: 'TEXT', priceCents: 0,
      });
      navigate(`/admin/practices/${p.id}?created=1`);
    } catch (e) { alert(e.message); setCreating(false); }
  };

  const counts = useAsync(() => api.memberCounts('practice').catch(() => ({})), []);

  const togglePublish = async (p) => {
    try {
      if (p.status === 'PUBLISHED') await api.adminUnpublishPractice(p.id);
      else await api.adminPublishPractice(p.id);
      reload();
    } catch (e) { if (!handleHasMembersError(e, 'practices', p.id)) alert(e?.data?.message || e.message); }
  };

  const priceLabel = (p) => (p.priceCents > 0
    ? `$${(p.priceCents / 100).toFixed(2)}`
    : 'Free');

  return (
    <div className="page">
      <PageHead
        icon="🧘"
        title="Manage your practices"
        sub="Open a practice to edit its details, content and price. Members and invites are under Members."
        actions={
          <button className="btn btn-primary" disabled={creating} onClick={newPractice}>
            {creating ? 'Creating…' : '+ New practice'}
          </button>
        }
      />
      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <Section style={{ padding: 0 }}>
          {data.length === 0 ? (
            <div style={{ padding: 18 }}><Empty>No practices yet.</Empty></div>
          ) : (
            <table className="tbl">
              <thead><tr><th>Practice</th><th>Type</th><th>Price</th><th>Members</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p.id} className="clickable" onClick={() => navigate(`/admin/practices/${p.id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <b>{p.title}</b>
                      {p.subtitle && <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{p.subtitle}</div>}
                    </td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{p.primaryType || 'TEXT'}</td>
                    <td>{p.priceCents > 0 ? <span className="pill pro">{priceLabel(p)}</span> : <span className="pill free">Free</span>}</td>
                    <td><MembersCountCell count={counts.loading ? undefined : (counts.data?.[p.id] ?? 0)} tab="practices" id={p.id} /></td>
                    <td>
                      <span className="pill" style={{
                        background: p.status === 'PUBLISHED' ? 'var(--green-bg)' : '#f3f4f6',
                        color: p.status === 'PUBLISHED' ? 'var(--green-text)' : 'var(--muted)',
                      }}>{p.status}</span>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`btn btn-sm ${p.status === 'PUBLISHED' ? '' : 'btn-primary'}`}
                        onClick={() => togglePublish(p)}
                      >
                        {p.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}
    </div>
  );
}

/**
 * Delete a practice. A practice with members can't be deleted (409
 * HAS_MEMBERS) — offer to open Manage members. Returns true if deleted.
 */
export async function deletePracticeWithConfirm(id) {
  try {
    await api.adminDeletePractice(id);
    return true;
  } catch (e) {
    const d = e.data;
    // Decided 2026-10-02 — remove members first in Manage members.
    if (handleHasMembersError(e, 'practices', id)) return false;
    alert(d?.message || e.message);
    return false;
  }
}
